import type { AssigningMatch, MatchPositionKey } from '@/types/assigning';

export const PENDING_ASSIGNMENT_STATUS = 'Pending Assignment';
export const OPEN_ASSIGNMENT_STATUS = 'Open';
export const UNCONFIRMED_ASSIGNMENT_STATUS = 'Unconfirmed';
export const ACCEPTED_ASSIGNMENT_STATUS = 'Accepted';

type AssignmentStatusKey = 'crStatus' | 'ar1Status' | 'ar2Status' | 'fourthStatus' | 'mentorStatus';
type AssignmentOfficialIdKey = 'centerId' | 'ar1Id' | 'ar2Id' | 'fourthId' | 'mentorId';

interface AssignmentPositionDefinition {
	officialKey: MatchPositionKey;
	officialIdKey: AssignmentOfficialIdKey;
	statusKey: AssignmentStatusKey;
}

export const ASSIGNMENT_POSITIONS: AssignmentPositionDefinition[] = [
	{ officialKey: 'center', officialIdKey: 'centerId', statusKey: 'crStatus' },
	{ officialKey: 'ar1', officialIdKey: 'ar1Id', statusKey: 'ar1Status' },
	{ officialKey: 'ar2', officialIdKey: 'ar2Id', statusKey: 'ar2Status' },
	{ officialKey: 'fourth', officialIdKey: 'fourthId', statusKey: 'fourthStatus' },
	{ officialKey: 'mentor', officialIdKey: 'mentorId', statusKey: 'mentorStatus' }
];

function stringValue(value: unknown): string {
	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
		return String(value);
	}

	return '';
}

function normalizedValue(value: unknown): string {
	return stringValue(value)
		.trim()
		.replace(/[\s-]+/g, '_')
		.toUpperCase();
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
	return Object.prototype.hasOwnProperty.call(record, key);
}

function isAssigned(value: unknown): boolean {
	return value != null && value !== '';
}

export function isHiddenMatchVisibility(visibility: unknown): boolean {
	return normalizedValue(visibility) === 'HIDDEN';
}

export function isPendingAssignmentStatus(status: unknown): boolean {
	return normalizedValue(status) === 'PENDING_ASSIGNMENT';
}

export function statusForAssignedOfficial(visibility: unknown, isPastMatch = false): string {
	if (isHiddenMatchVisibility(visibility)) {
		return PENDING_ASSIGNMENT_STATUS;
	}

	return isPastMatch ? ACCEPTED_ASSIGNMENT_STATUS : UNCONFIRMED_ASSIGNMENT_STATUS;
}

export function assignmentIconStatus(status: unknown): string {
	switch (normalizedValue(status)) {
		case '':
		case 'NULL':
		case 'OPEN':
		case 'PENDING_ASSIGNMENT':
			return OPEN_ASSIGNMENT_STATUS;
		case 'ACCEPT':
		case 'ACCEPTED':
			return ACCEPTED_ASSIGNMENT_STATUS;
		case 'PENDING':
			return 'Pending';
		case 'UNCONFIRMED':
			return UNCONFIRMED_ASSIGNMENT_STATUS;
		case 'REJECTED':
		case 'TURNBACK':
		case 'TURNED_BACK':
			return 'Rejected';
		default:
			return stringValue(status) || OPEN_ASSIGNMENT_STATUS;
	}
}

export function buildVisibilityAssignmentStatusPatch(match: Partial<AssigningMatch> = {}, patch: Partial<AssigningMatch> = {}): Partial<AssigningMatch> {
	const matchRecord = match as Record<string, unknown>;
	const patchRecord = patch as Record<string, unknown>;
	const effectiveVisibility = hasOwn(patchRecord, 'visibility') ? patchRecord['visibility'] : match.visibility;
	const hidden = isHiddenMatchVisibility(effectiveVisibility);
	const statusPatch: Partial<AssigningMatch> = {};

	for (const position of ASSIGNMENT_POSITIONS) {
		let official: unknown;

		if (hasOwn(patchRecord, position.officialIdKey)) {
			official = patchRecord[position.officialIdKey];
		} else if (hasOwn(patchRecord, position.officialKey)) {
			official = patchRecord[position.officialKey];
		} else {
			official = matchRecord[position.officialKey];
		}

		if (!isAssigned(official)) {
			continue;
		}

		const effectiveStatus = hasOwn(patchRecord, position.statusKey) ? patchRecord[position.statusKey] : matchRecord[position.statusKey];

		if (hidden) {
			statusPatch[position.statusKey] = PENDING_ASSIGNMENT_STATUS;
		} else if (isPendingAssignmentStatus(effectiveStatus)) {
			statusPatch[position.statusKey] = UNCONFIRMED_ASSIGNMENT_STATUS;
		}
	}

	return statusPatch;
}
